import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from './Button';
interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
}
export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'warning'
}: ConfirmModalProps) {
  const colors = {
    danger: {
      bg: 'from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20',
      border: 'border-red-200 dark:border-red-800',
      icon: 'bg-red-500',
      button: 'bg-red-500 hover:bg-red-600'
    },
    warning: {
      bg: 'from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20',
      border: 'border-yellow-200 dark:border-yellow-800',
      icon: 'bg-yellow-500',
      button: 'bg-yellow-500 hover:bg-yellow-600'
    },
    info: {
      bg: 'from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20',
      border: 'border-blue-200 dark:border-blue-800',
      icon: 'bg-blue-500',
      button: 'bg-blue-500 hover:bg-blue-600'
    }
  };
  const style = colors[variant];
  return <AnimatePresence data-id="element-177">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6" onClick={onClose} data-id="element-178">
          <motion.div initial={{
        scale: 0.95,
        y: 20
      }} animate={{
        scale: 1,
        y: 0
      }} exit={{
        scale: 0.95,
        y: 20
      }} className={`bg-gradient-to-br ${style.bg} border-2 ${style.border} rounded-3xl p-6 max-w-md w-full shadow-2xl`} onClick={e => e.stopPropagation()} data-id="element-179">
            <div className="flex items-start gap-4 mb-6" data-id="element-180">
              <div className={`w-12 h-12 ${style.icon} rounded-2xl flex items-center justify-center flex-shrink-0`} data-id="element-181">
                <AlertTriangle className="w-6 h-6 text-white" data-id="element-182" />
              </div>
              <div className="flex-1" data-id="element-183">
                <h3 className="font-fredoka font-bold text-xl text-game-dark dark:text-white mb-2" data-id="element-184">
                  {title}
                </h3>
                <p className="font-quicksand text-gray-700 dark:text-gray-300 leading-relaxed" data-id="element-185">
                  {message}
                </p>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors" data-id="element-186">
                <X className="w-5 h-5" data-id="element-187" />
              </button>
            </div>

            <div className="flex gap-3" data-id="element-188">
              <Button variant="secondary" onClick={onClose} className="flex-1" data-id="element-189">
                {cancelText}
              </Button>
              <Button onClick={() => {
            onConfirm();
            onClose();
          }} className={`flex-1 ${style.button}`} data-id="element-190">
                {confirmText}
              </Button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}